import type { Models } from '@smartlinkdev/types';
import axios from 'axios';
import { compareAsc, parseISO } from 'date-fns';
import { alphabetical, unique } from 'radash';
import { PUBLIC_API_URL, PUBLIC_SETTINGS_BUCKET } from '$env/static/public';
import { cloneDeepArray } from '$lib/utils';
import type { APIExport, EnhancedExport } from '$lib/types';

export class RoveIQService {
  private _fetch: typeof fetch;

  public constructor(svelteFetch: typeof fetch) {
    this._fetch = svelteFetch;
  }

  public async fetchExport(venueId: number): Promise<EnhancedExport> {
    let apiExport: APIExport;
    if (PUBLIC_SETTINGS_BUCKET) {
      const settingsFilesNeeded: string[] = [
        `venue-${venueId}/locations.json`,
        `venue-${venueId}/map.json`,
        `venue-${venueId}/venue.json`,
        `venue-${venueId}/translations.json`
      ];
      const requests = settingsFilesNeeded.map(url => {
        const endpoint = `${PUBLIC_SETTINGS_BUCKET}/${url}`;
        return fetch(endpoint);
      });
      const responses = await Promise.all(requests);
      const converts = responses.map((response) => {
        return response.json();
      });
      const jsons = await Promise.all(converts);
      const allSettings: {[key: string]:any} = {};
      jsons.forEach(setting => {
        Object.keys(setting).forEach((key) => {
          allSettings[key] = setting[key];
        });
      });
      apiExport = allSettings as APIExport;
    } else {
      const endpoint = `${PUBLIC_API_URL}/v2/venues/${venueId}/export/mobile`;
      const res = await fetch(endpoint);
      const roveAPIResponse = await res.json();
      apiExport = roveAPIResponse.data as APIExport;
    }

    const sortedAmenities = this.sortAmenities(apiExport.amenities);
    const amenityCategories = this.extractAmenityCategories(sortedAmenities);

    const sortedEvents = this.sortEvents(apiExport.events);

    const sortedLocations = this.sortLocations(apiExport.locations);
    const locationCategories = this.extractLocationCategories(sortedLocations);

    return {
      amenities: sortedAmenities,
      events: sortedEvents,
      locations: sortedLocations,
      venue: apiExport.venue,
      amenityCategories,
      locationCategories,
      floor_transitions: apiExport.floor_transitions,
      floors: apiExport.floors,
      lighting: apiExport.lighting,
      map_backgrounds: apiExport.map_backgrounds,
      map_suites: apiExport.map_suites,
      venue_languages: apiExport.venue_languages,
    };
  }

  public async fetchTranslations(venueId: number): Promise<Models.Translation[]> {
    const endpoint = `https://s3.us-east-2.amazonaws.com/settings.roveiq.com/venue-${venueId}/translations.json`;
    try {
      const res = await axios.get<{ translations: Models.Translation[] }>(endpoint);
      if (res.status === 200) {
        return res.data.translations;
      }
      return Promise.reject(new Error('error fetching translations'));
    } catch (e) {
      console.log('error fetching translations:', e);
      return Promise.reject(e);
    }
    return [];
  }

  private extractAmenityCategories(
    amenities: Models.Amenity[]
  ): Models.Amenity[] {
    const allAmenityCategories: Models.Category[] = [];
    amenities.forEach((amenity: Models.Amenity): void => {
      if (amenity.amenity_types && amenity.amenity_types.length > 0) {
        allAmenityCategories.push(...amenity.amenity_types);
      }
    });
    const uniqueCats = unique(allAmenityCategories, (c) => c.id);
    return this.sortCategories(uniqueCats);
  }

  private extractLocationCategories(
    locations: Models.Location[]
  ): Models.Location[] {
    const allLocationCategories: Models.Category[] = [];
    locations.forEach((location: Models.Location): void => {
      if (location.location_types && location.location_types.length > 0) {
        allLocationCategories.push(...location.location_types);
      }
    });
    const uniqueCats = unique(allLocationCategories, (c) => c.id);
    return this.sortCategories(uniqueCats);
  }

  private sortAmenities(amenities: Models.Amenity[]): Models.Amenity[] {
    return alphabetical(amenities, (a: Models.Amenity) => a.name);
  }

  private sortCategories(cats: Models.Category[]): Models.Category[] {
    return alphabetical(cats, (c) => c.name);
  }

  private sortEvents(events: Models.Event[]): Models.Event[] {
    const eventsCopy = cloneDeepArray(events);
    return eventsCopy.sort((a: Models.Event, b: Models.Event) =>
      compareAsc(parseISO(a.start_date), parseISO(b.start_date))
    );
  }

  private sortLocations(locations: Models.Location[]): Models.Location[] {
    return alphabetical(locations, (l: Models.Location) => l.name);
  }
}
